export type Plan = {
  name: string;
  id: string;
  type: 'per_site_pricing' | 'per_owner_pricing';
  support_level: number;
};

export type Site = {
  id: string;
  primary_environment_id: string;
  name: string;
  internal_subdomain: string;
  deactivated: boolean;
  usage_blocked: boolean;
  current_account: {
    site_enter_url: string;
    relationship: 'owner' | 'collaborator' | 'organization_member' | 'sso_user';
  };
  owner?: {
    type: 'account' | 'organization';
    id: string;
    title: string;
    dashboard_enter_url: string;
    site_enter_url: string;
    is_agency_partner: boolean;
  };
  upload_storage_settings: UploadStorageSettings;
  plan: Plan;
};

export type Organization = {
  id: string;
  name: string;
  chargebee_customer_id: string | null;
  per_owner_pricing_adjuster_ttl: number | null;
  current_account: {
    dashboard_enter_url: string;
  };
  owner: {
    dashboard_enter_url: string;
  };
  plan: Plan;
  sites: Site[];
  is_agency_partner: boolean;
  appointed_organizations: Array<{ name: string; id: string }>;
  referrer_organization: { name: string; id: string } | null;
};

export type Account = {
  email: string;
  name: string | null;
  company: string | null;
  id: string;
  dashboard_enter_url: string;
  chargebee_customer_id: string | null;
  sites: Site[];
  organizations: Organization[];
  max_support_level: number;
  plan?: Plan;
  per_owner_pricing_adjuster_ttl?: number | null;
};

// SSO users are not linked to an account: one email has an SSO user for each
// project where it logs in with SSO.
export type SsoUser = {
  email: string;
  name: string;
  sites: Site[];
  max_support_level: number;
};

export type ProfileResponse = {
  account: Account | null;
  sso_user?: SsoUser | null;
  related_accounts: Account[];
};

export const levelEmoji: Record<string, string> = {
  '3': '🚄',
  '2': '🚗',
  '1': '🛴',
};

export type NativeAdapter = {
  type: 'native';
};

export type AwsS3AccessKeyAdapter = {
  type: 'aws_s3_access_key';
  bucket: string;
  region: string;
  access_key_id: string;
  access_key_secret: string;
  acl: null | 'private' | 'public-read';
};

export type AwsS3AssumeRoleAdapter = {
  type: 'aws_s3_assume_role';
  bucket: string;
  region: string;
  role_arn: string;
  external_id: string;
  acl: null | 'private' | 'public-read';
};

export type GcpStorageAdapter = {
  type: 'gcp_storage';
  bucket: string;
  interoperable_key_id: string;
  interoperable_key_secret: string;
  service_account_key: {
    type: string;
    project_id: string;
    private_key_id: string;
    private_key: string;
    client_email: string;
    client_id: string;
    auth_uri: string;
    token_uri: string;
    auth_provider_x509_cert_url: string;
    client_x509_cert_url: string;
    universe_domain?: string;
  };
};

export type CloudflareR2Adapter = {
  type: 'cloudflare_r2';
  bucket: string;
  access_key_id: string;
  access_key_secret: string;
  s3_api_host: string;
};

export type AzureBlobStorageAccessKeyAdapter = {
  type: 'azure_blob_storage_access_key';
  storage_account: string;
  container: string;
  storage_account_access_key: string;
};

export type AzureBlobStorageUserDelegationAdapter = {
  type: 'azure_blob_storage_user_delegation';
  storage_account: string;
  container: string;
  app_tenant_id: string;
  app_client_id: string;
  app_client_secret: string;
};

export type UploadStorageSettings = {
  adapter:
    | NativeAdapter
    | AwsS3AccessKeyAdapter
    | AwsS3AssumeRoleAdapter
    | GcpStorageAdapter
    | CloudflareR2Adapter
    | AzureBlobStorageAccessKeyAdapter
    | AzureBlobStorageUserDelegationAdapter;
  url_template: string;
  path_template: string;
};
